import { useEffectWithController, usePromise } from '@carvana/vdp-react-hooks';
import { createContext, useMemo, useState } from 'react';
import { useUser } from '~/contexts/UserContext';
import type { SavedSearch } from '~/contracts';
import { readSavedSearch } from '~/services/savedSearch';
import generateErrorLogger from '~/utilities/errorHandlers';

export type SavedSearchesContextType = {
  savedSearches: SavedSearch[];
  setSavedSearches: (savedSearches: SavedSearch[]) => void;
};

const defaultContext: SavedSearchesContextType = {
  savedSearches: [],
  setSavedSearches() {}
};

const SavedSearchesContext =
  createContext<SavedSearchesContextType>(defaultContext);

export function SavedSearchesContextProvider({ children }) {
  const mounted = usePromise();
  const [savedSearches, setSavedSearches] = useState(
    defaultContext.savedSearches
  );
  const [dataFetched, setDataFetched] = useState(false);
  const {
    data: { email, isLoggedIn },
    isLoaded
  } = useUser();

  useEffectWithController(
    controller => {
      if (!email || !isLoaded || !isLoggedIn || dataFetched) return;

      mounted(readSavedSearch(email, controller))
        .then(({ data }) => {
          setSavedSearches(data.savedSearches ?? []);
          setDataFetched(true);
        })
        .catch(generateErrorLogger('SavedSearches'));
    },
    [dataFetched, email, isLoaded, isLoggedIn, mounted]
  );

  const value = useMemo<SavedSearchesContextType>(
    () => ({
      savedSearches,
      setSavedSearches
    }),
    [savedSearches, setSavedSearches]
  );

  return (
    <SavedSearchesContext.Provider value={value}>
      {children}
    </SavedSearchesContext.Provider>
  );
}

SavedSearchesContextProvider.displayName = 'SavedSearchesContextProvider';

export default SavedSearchesContext;
